import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert, Box, Button, Chip, CircularProgress, MenuItem,
  Paper, Select, Stack, Typography,
} from '@mui/material';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';

import useRole from '../../../auth/useRole';
import AppToast from '../../../shared/components/AppToast';
import LabeledField from '../../../shared/components/LabeledField';
import { resolveUserFacingError } from '../../../shared/utils/actionFeedback';
import { triggerBrowserDownload } from '../../../shared/utils/download';
import { isHistoricalViewerYear } from '../../../shared/utils/reportingYear';
import { usePagePresentation } from '../../../context/PagePresentationContext';
import EmissionOverviewNotice from '../../emissions/components/EmissionOverviewNotice';
import useReportsMaster from '../hooks/useReportsMaster';
import useReportBreakdown from '../hooks/useReportBreakdown';
import ReportExportDialog from './ReportExportDialog';
import ReportBreakdownTable from './ReportBreakdownTable';

export default function ReportsPage() {
  const { t } = useTranslation();
  const { isAdmin } = useRole();
  const {
    years,
    year,
    setYear,
    reload,
    report,
    exporting,
    exportReport,
    loading,
    error,
    meta,
    historicalReport,
  } = useReportsMaster();
  const [toast, setToast] = useState(null);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const isHistorical = isHistoricalViewerYear(year);
  const breakdown = useReportBreakdown({
    year,
    enabled: !loading && (error?.message === 'NO_PUBLISHED_DATA' || !error)
      && (!isHistorical || Boolean(historicalReport)),
    historicalReport,
    fallbackRows: report.rows,
    fallbackTotal: Number(report.total) || 0,
    fallbackTotalPercentage: Number(report.totalPercentage) || 0,
  });
  usePagePresentation({
    loading,
    status: error ? null : meta.status,
    estimation: error ? null : meta.estimatedFactors,
  });
  const isNoPublishedData = error?.message === 'NO_PUBLISHED_DATA';
  const canRenderData = !loading && (isNoPublishedData || !error);
  const hasTotalOnlyScope = Object.values(meta.scopeCoverage || {}).includes('TOTAL_ONLY');

  const handleExport = async (exportMode) => {
    try {
      const file = await exportReport(exportMode);
      triggerBrowserDownload({ href: file.href, fileName: file.fileName });
      setTimeout(() => URL.revokeObjectURL(file.href), 0);
      setExportDialogOpen(false);
      setToast({
        severity: 'success',
        message: t('reports.toast.exported', { year: file.fiscalYear }),
      });
    } catch (exportError) {
      if (!exportError?.fieldErrors?.length) {
        setToast({
          severity: 'error',
          message: resolveUserFacingError(exportError, t('reports.exportFailed')),
        });
      }
      throw exportError;
    }
  };

  return (
    <Box component="section" aria-labelledby="reports-title" className="page-stack">
      <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          alignItems={{ xs: 'stretch', md: 'center' }}
          justifyContent="space-between"
          spacing={2}
        >
          <Stack spacing={0.75} sx={{ minWidth: 0 }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <AssessmentOutlinedIcon color="success" />
              <Typography id="reports-title" component="h1" variant="h6" sx={{ fontWeight: 800, fontSize: 22 }}>
                {t('reports.title')}
              </Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: 13 }}>
              {t('reports.subtitle')}
            </Typography>
            <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
              {report.standards.map((standard) => (
                <Chip key={standard.id} label={t(standard.labelKey)} size="small" variant="outlined" />
              ))}
            </Stack>
          </Stack>

          <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'flex-end' }} spacing={1.25}>
            <LabeledField label={t('reports.reportingYear')} htmlFor="reports-year" sx={{ minWidth: { sm: 142 } }}>
              <Select
                id="reports-year"
                size="small"
                value={year}
                onChange={(event) => setYear(Number(event.target.value))}
                disabled={exporting}
                inputProps={{ 'aria-label': t('reports.reportingYear') }}
              >
                {years.map((item) => <MenuItem key={item} value={item}>{item}</MenuItem>)}
              </Select>
            </LabeledField>
            <Button
              variant="contained"
              color="success"
              startIcon={exporting ? <CircularProgress color="inherit" size={15} /> : <FileDownloadOutlinedIcon />}
              onClick={() => setExportDialogOpen(true)}
              disabled={exporting || loading || Boolean(error) || isHistorical}
              sx={{ flexShrink: 0, minHeight: 40, minWidth: 'max-content', whiteSpace: 'nowrap' }}
            >
              {exporting ? t('reports.exporting') : t('reports.exportAction')}
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {isHistorical && !loading && !error && (
        <Alert severity="info" sx={{ alignItems: 'center' }}>
          <Stack spacing={0.75}>
            <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
              <Chip label={t('reports.historical.badge')} size="small" variant="outlined" />
              <Typography variant="body2">{t('reports.historical.viewOnly')}</Typography>
            </Stack>
            {!historicalReport && (
              <Typography variant="body2">{t('reports.historical.noReport')}</Typography>
            )}
            {historicalReport && hasTotalOnlyScope && (
              <Typography variant="body2">{t('reports.historical.totalOnly')}</Typography>
            )}
            {historicalReport && meta.isPlaceholderData && (
              <Typography variant="body2">{t('reports.historical.placeholder')}</Typography>
            )}
          </Stack>
        </Alert>
      )}

      <EmissionOverviewNotice
        error={error}
        hasData={meta.hasData}
        onRetry={reload}
      />

      {canRenderData && (
        <Paper component="section" variant="outlined" aria-labelledby="report-table-title">
          {breakdown.loading ? (
            <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress />
            </Box>
          ) : breakdown.error ? (
            <Alert severity="error" sx={{ m: 2 }}>
              {t('reports.loadError')}
            </Alert>
          ) : (
            <ReportBreakdownTable
              rows={breakdown.rows}
              total={breakdown.total}
              totalPercentage={breakdown.totalPercentage}
            />
          )}
        </Paper>
      )}


      <ReportExportDialog
        open={exportDialogOpen}
        exporting={exporting}
        isAdmin={isAdmin}
        hasData={meta.hasData}
        anomalies={meta.anomalies}
        onExport={handleExport}
        onClose={() => setExportDialogOpen(false)}
      />
      <AppToast toast={toast} onClose={() => setToast(null)} />
    </Box>
  );
}
